import dayjs from '../timezone/dayjsInit';
import { getCurrentTz } from '../timezone';

// Shared time range options for all components.
//
// <b>Ladder is dense by design.</b> Plans land on a wide range of
// retention windows (4d / 5d / 7d / 14d / 30d / 60d / 90d / 180d / 365d),
// so a sparse "7d → 30d" jump means a 14-day plan would see "Last 7
// days" as its biggest option even though it could query twice that.
// We add 2/3/5/14/60/90 explicitly so every common retention has a
// "Last <full window>" preset; plans with non-standard retention
// (e.g. 21d) get a synthetic option appended by the picker.
export const TIME_RANGE_OPTIONS = [
  { value: '5m', label: 'Last 5 minutes' },
  { value: '15m', label: 'Last 15 minutes' },
  { value: '30m', label: 'Last 30 minutes' },
  { value: '1h', label: 'Last 1 hour' },
  { value: '3h', label: 'Last 3 hours' },
  { value: '6h', label: 'Last 6 hours' },
  { value: '12h', label: 'Last 12 hours' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '2d',  label: 'Last 2 days' },
  { value: '3d',  label: 'Last 3 days' },
  { value: '5d',  label: 'Last 5 days' },
  { value: '7d',  label: 'Last 7 days' },
  { value: '14d', label: 'Last 14 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '60d', label: 'Last 60 days' },
  { value: '90d', label: 'Last 90 days' },
];

// Preset options for quick select chips. {@code getRange} returns the
// {@code [from, to]} dayjs pair *in the user's timezone* so the chip
// values render in their wall-clock when a custom range is picked.
// The underlying epoch-ms are unaffected by tz — they're absolute
// instants on the wire.
export const TIME_RANGE_PRESETS = [
  { value: '1h',  label: 'Last 1h',  getRange: () => [dayjs.tz(undefined, getCurrentTz()).subtract(1, 'hour'),  dayjs.tz(undefined, getCurrentTz())] },
  { value: '6h',  label: 'Last 6h',  getRange: () => [dayjs.tz(undefined, getCurrentTz()).subtract(6, 'hour'),  dayjs.tz(undefined, getCurrentTz())] },
  { value: '24h', label: 'Last 24h', getRange: () => [dayjs.tz(undefined, getCurrentTz()).subtract(24, 'hour'), dayjs.tz(undefined, getCurrentTz())] },
  { value: '7d',  label: 'Last 7d',  getRange: () => [dayjs.tz(undefined, getCurrentTz()).subtract(7, 'day'),   dayjs.tz(undefined, getCurrentTz())] },
  { value: '30d', label: 'Last 30d', getRange: () => [dayjs.tz(undefined, getCurrentTz()).subtract(30, 'day'),  dayjs.tz(undefined, getCurrentTz())] },
];

export const DEFAULT_TIME_RANGE = '1h';

// Convert time range value to milliseconds for custom ranges
export const getTimeRangeMillis = (value) => {
  const matches = value.match(/^(\d+)([mhd])$/);
  if (!matches) return null;

  const [, num, unit] = matches;
  const amount = parseInt(num, 10);

  switch (unit) {
    case 'm': return amount * 60 * 1000;
    case 'h': return amount * 60 * 60 * 1000;
    case 'd': return amount * 24 * 60 * 60 * 1000;
    default: return null;
  }
};

// Get start and end times based on time range value. The returned
// dayjs objects are tz-aware (anchored to the user's preferred zone)
// so wall-clock semantics like "Today" / "Yesterday" / "Last 24 h"
// align with the user's local clock. The underlying instants (via
// {@code .valueOf()}) are tz-independent epoch-millis, so the
// backend query parameters don't change shape.
/**
 * @param {string} value  the range token, e.g. "1h" or "custom:<start>:<end>"
 * @param {number} [atEpochMs]  the instant to resolve "now" to.
 *
 * §6 asks for one shared `now` per refresh cycle, and the reason is concrete: a 24-widget
 * dashboard called this 24 times, so each widget resolved its own `now` milliseconds apart and
 * asked about a slightly different window. Two panels showing the same metric then disagree at
 * the right-hand edge, and a shared query cannot be coalesced because the windows differ by a
 * few milliseconds — which defeats the deduplication meant to make the dashboard cheap.
 *
 * Passing the cycle's instant fixes both. Omitting it keeps the old behaviour for the many
 * callers where a single resolution is all there is.
 */
export const getTimeRangeDates = (value, atEpochMs) => {
  const tz = getCurrentTz();
  const now = (atEpochMs === undefined || atEpochMs === null ? dayjs() : dayjs(atEpochMs)).tz(tz);

  // Handle custom range format — start/end are stored as epoch-ms.
  if (value?.startsWith('custom:')) {
    const [, start, end] = value.split(':');
    return {
      start: dayjs(parseInt(start)).tz(tz),
      end:   dayjs(parseInt(end)).tz(tz),
    };
  }

  const millis = getTimeRangeMillis(value);
  if (!millis) {
    // Default to 1 hour if invalid
    return {
      start: now.subtract(1, 'hour'),
      end:   now,
    };
  }

  return {
    start: now.subtract(millis, 'millisecond'),
    end:   now,
  };
};

// Get display label for a time range value
export const getTimeRangeLabel = (value) => {
  if (value?.startsWith('custom:')) {
    const [, start, end] = value.split(':');
    const tz = getCurrentTz();
    return `${dayjs(parseInt(start)).tz(tz).format('MMM D')} - ${dayjs(parseInt(end)).tz(tz).format('MMM D')}`;
  }

  const option = TIME_RANGE_OPTIONS.find(opt => opt.value === value);
  return option?.label || value;
};

// ── Retention-aware helpers ──────────────────────────────────────────
//
// These let the picker filter / clamp without re-deriving anything
// from scratch. The TIME_RANGE_OPTIONS array is static and already
// sorted shortest-to-longest, so a single sequential walk is all we
// ever need.

/**
 * Convert a time-range value into days as a float. Used for filtering
 * the static options list against a retention cap. Returns 0 for
 * inputs we can't parse (defensive — we never want to accidentally
 * hide every option because of a string typo).
 */
export const parseToDays = (value) => {
  if (!value) return 0;
  if (value.startsWith('custom:')) {
    const [, start, end] = value.split(':');
    const ms = Math.max(0, parseInt(end, 10) - parseInt(start, 10));
    return ms / (24 * 60 * 60 * 1000);
  }
  const ms = getTimeRangeMillis(value);
  return ms ? ms / (24 * 60 * 60 * 1000) : 0;
};

/**
 * Walk TIME_RANGE_OPTIONS once and return only those that fit inside
 * `maxDays`. Cheap — array has ~16 entries.
 */
export const filterOptionsByMaxDays = (options, maxDays) => {
  if (!maxDays || maxDays <= 0) return options;
  return options.filter((opt) => parseToDays(opt.value) <= maxDays);
};

/**
 * Given a retention cap in days, return the time-range options the
 * picker should offer: every static option that fits, plus a
 * synthetic "Last {maxDays} days" entry at the cap if the cap
 * doesn't already line up with a static option.
 *
 * <p>Why synthesise: customer plans land on any retention number we
 * decide to support (21, 45, 120, 365). Hardcoding every value into
 * the static list would clutter the picker for short-retention plans
 * AND still miss the long tail. Adding a single entry at the cap
 * means each user sees a clean "Last <my retention>" preset that
 * exactly matches their plan, regardless of which value the operator
 * picked.
 */
export const optionsForMaxDays = (maxDays) => {
  const opts = filterOptionsByMaxDays(TIME_RANGE_OPTIONS, maxDays);
  if (!maxDays || maxDays <= 0) return opts;
  const hasExact = opts.some((o) => Math.abs(parseToDays(o.value) - maxDays) < 0.01);
  if (hasExact) return opts;
  return [
    ...opts,
    { value: `${Math.floor(maxDays)}d`, label: `Last ${Math.floor(maxDays)} days` },
  ];
};

/**
 * If the supplied value exceeds `maxDays`, return the largest preset
 * that fits. If nothing fits (very short retention), return the
 * smallest preset. Custom ranges are bounded by clipping the start
 * to `now() - maxDays`.
 *
 * Cheap — one walk through a 10-element list.
 */
export const clampToMaxDays = (value, maxDays) => {
  if (!maxDays || maxDays <= 0) return value;
  const days = parseToDays(value);
  if (days <= maxDays) return value;

  if (value?.startsWith('custom:')) {
    const [, start, end] = value.split(':');
    const endMs = parseInt(end, 10);
    const minStart = Math.max(parseInt(start, 10), endMs - maxDays * 24 * 60 * 60 * 1000);
    return `custom:${minStart}:${endMs}`;
  }

  // Walk options shortest → longest, remember the largest that fits.
  let best = null;
  for (const opt of TIME_RANGE_OPTIONS) {
    if (parseToDays(opt.value) <= maxDays) best = opt.value;
    else break;
  }
  return best || TIME_RANGE_OPTIONS[0].value;
};
